import { useEffect, useMemo, useState } from 'react'
import { youtubeController, YT_STATE } from 'eyecontact'
import { useEyeContact } from 'eyecontact/react'
import { useYouTubePlayer } from '../hooks/useYouTubePlayer'
import { AttentionBadge } from './AttentionBadge'
import { CameraPreview } from './CameraPreview'
import { EventLog } from './EventLog'
import { SettingsPanel } from './SettingsPanel'
import { ThemeToggle } from './ThemeToggle'
import { UrlBar, VideoStage } from './VideoStage'
import { Button, Panel, TooltipProvider, Tooltip } from './ui'
import { formatMs } from '../lib/cn'
import { DEFAULT_SETTINGS, clearSettings, loadSettings, saveSettings, type Settings } from '../lib/settings'
import type { MediaPipeAssets } from '../lib/assets'

// Placeholder starting video. Swap this for whatever you want to demo with —
// note that plenty of videos have embedding disabled by their owner, in which
// case the player reports error 101/150 and you'll need a different one.
const DEFAULT_VIDEO = 'YE7VzlLtp-4'

export function Studio({ assets }: { assets: MediaPipeAssets }) {
  // Read storage once, lazily. Doing it in the initialiser rather than an
  // effect means the gate is constructed with the viewer's own numbers instead
  // of booting on defaults and being reconfigured a frame later.
  const [settings, setSettings] = useState<Settings>(loadSettings)
  const [initialSettings] = useState(settings)

  const gaze = useEyeContact({
    mode: initialSettings.mode,
    graceMs: initialSettings.graceMs,
    resumeMs: initialSettings.resumeMs,
    fps: initialSettings.fps,
    pauseWhenTabHidden: initialSettings.pauseWhenTabHidden,
    pauseWhenWindowBlurred: initialSettings.pauseWhenWindowBlurred,
    emitLandmarks: true,
    logLevel: 'debug',
    signalHz: 12,
    ...(assets.wasmBasePath ? { wasmBasePath: assets.wasmBasePath } : {}),
    ...(assets.modelAssetPath ? { modelAssetPath: assets.modelAssetPath } : {}),
  })

  const yt = useYouTubePlayer(DEFAULT_VIDEO)

  const { setMode, setTiming, setThresholds, gate } = gaze

  // Wire the player into the gate once both exist. The deps already ensure
  // this runs exactly once per (gate, player) pair; an extra "have I attached
  // this player before" ref would block the re-attach that the cleanup makes
  // necessary when the gate is rebuilt, and the video would silently stop
  // pausing.
  useEffect(() => {
    if (!gate || !yt.player) return
    const controller = youtubeController(yt.player)
    const detach = gate.attach(controller)
    return () => {
      detach()
      controller.destroy?.()
    }
  }, [gate, yt.player])

  // Push settings changes down. Timing and thresholds are live; fps and
  // landmark emission are read at start, hence the restart note in the UI.
  //
  // Depend on the individual setters, not on `gaze`. The hook returns a fresh
  // object every time face signals update (12Hz), so depending on the whole
  // thing re-runs every one of these effects twelve times a second.

  useEffect(() => {
    setMode(settings.mode)
  }, [settings.mode, setMode])

  useEffect(() => {
    setTiming({ graceMs: settings.graceMs, resumeMs: settings.resumeMs })
  }, [settings.graceMs, settings.resumeMs, setTiming])

  useEffect(() => {
    setThresholds(settings.thresholds)
  }, [settings.thresholds, setThresholds])

  useEffect(() => {
    gate?.setFps(settings.fps)
  }, [settings.fps, gate])

  // Persist whatever the viewer lands on. Slider drags fire this often, but a
  // localStorage write of a couple of hundred bytes is far cheaper than the
  // detection frame already running alongside it, so there is nothing to gain
  // from debouncing it.
  useEffect(() => {
    saveSettings(settings)
  }, [settings])

  const patch = (p: Partial<Settings>) => setSettings((s) => ({ ...s, ...p }))

  const resetSettings = () => {
    clearSettings()
    setSettings({ ...DEFAULT_SETTINGS, thresholds: { ...DEFAULT_SETTINGS.thresholds } })
  }

  const startCamera = () => void gaze.start().catch(() => {})

  // Belt and braces behind the click blocker in VideoStage. The blocker stops
  // a click reaching the iframe, but playback can also be started by the
  // keyboard, by an autoplay parameter on a pasted URL, or by YouTube's own
  // end-screen suggestions. Whatever the route, if the camera is not watching
  // then nothing is going to pause this, so stop it.
  useEffect(() => {
    if (gaze.running || !yt.player) return
    if (yt.playerState === YT_STATE.PLAYING || yt.playerState === YT_STATE.BUFFERING) {
      yt.player.pauseVideo()
    }
  }, [gaze.running, yt.player, yt.playerState])

  const playing = yt.playerState === YT_STATE.PLAYING || yt.playerState === YT_STATE.BUFFERING
  const gatePaused = gaze.state === 'away'

  // MediaPipe errors carry a full C++ stack trace. Show a sentence people can
  // act on and tuck the raw text behind a disclosure for whoever needs it.
  const errorInfo = useMemo(() => {
    if (!gaze.error) return null
    const raw = gaze.error.message
    let summary = raw
    if (/permission|NotAllowed/i.test(raw)) {
      summary = 'Camera permission was denied. Allow it in the address bar, then start again.'
    } else if (/NotFound|no camera|DevicesNotFound/i.test(raw)) {
      summary = 'No camera found on this device.'
    } else if (/NotReadable|TrackStart/i.test(raw)) {
      summary = 'The camera is in use by another app. Close it and try again.'
    } else if (/secure context/i.test(raw)) {
      summary = 'The camera needs a secure context: https, or http on localhost.'
    } else if (/MediaPipe failed to load|zip archive/i.test(raw)) {
      summary =
        'The face detection model could not be loaded. It is usually a blocked CDN — ' +
        'run `npm run sync-assets` to serve it from this origin instead.'
    } else if (raw.length > 180) {
      summary = `${raw.slice(0, 180).trimEnd()}…`
    }
    return { summary, raw, truncated: summary !== raw }
  }, [gaze.error])

  return (
    <TooltipProvider>
      <div className="app-shell min-h-full">
        <div className="mx-auto max-w-[1440px] px-5 py-5">
          <header className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-baseline gap-3">
              <h1 className="font-mono text-base font-semibold tracking-tight">
                eye<span className="text-accent">contact</span>
              </h1>
              <p className="text-2xs text-faint">Playback that stops when you stop watching</p>
            </div>

            <div className="flex items-center gap-2.5">
              <AttentionBadge
                state={gaze.state}
                reason={gaze.reason}
                awayForMs={gaze.awayForMs}
                graceMs={settings.graceMs}
              />
              {gaze.running ? (
                <Button variant="danger" onClick={gaze.stop}>
                  Stop camera
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={startCamera}
                  disabled={gaze.state === 'starting'}
                >
                  {gaze.state === 'starting' ? 'Starting…' : 'Start camera'}
                </Button>
              )}
              <ThemeToggle />
            </div>
          </header>

          {errorInfo && (
            <div
              role="alert"
              className="mb-4 rounded-md border border-danger/40 bg-danger/10 px-3.5 py-2.5 text-xs text-danger"
            >
              <p>{errorInfo.summary}</p>
              {errorInfo.truncated && (
                <details className="mt-1.5">
                  <summary className="cursor-pointer text-2xs text-danger/70 hover:text-danger">
                    Full error
                  </summary>
                  <pre className="scroll-thin mt-1.5 max-h-32 overflow-auto whitespace-pre-wrap break-words font-mono text-2xs text-danger/80">
                    {errorInfo.raw}
                  </pre>
                </details>
              )}
            </div>
          )}

          {assets.note && !errorInfo && (
            <p className="mb-4 rounded-md border border-line bg-surface px-3.5 py-2 text-2xs text-faint">
              {assets.note}
            </p>
          )}

          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="space-y-4">
              <VideoStage
                containerRef={yt.containerRef}
                paused={gatePaused && gaze.running}
                ready={yt.ready}
                error={yt.error}
                cameraOn={gaze.running}
                recoveryProgress={gaze.recoveryProgress}
                reason={gaze.reason}
                onStartCamera={startCamera}
              />

              <UrlBar onLoad={yt.load} disabled={!yt.ready} />

              <Panel title="Event log" active={gaze.running}>
                <EventLog entries={gaze.logs} />
              </Panel>

              <Panel title="Drop it into your own project">
                <div className="space-y-3 p-3.5">
                  <pre className="scroll-thin overflow-x-auto rounded-md border border-line bg-surface-2 p-3 font-mono text-2xs leading-relaxed text-muted">
{`import { EyeContact, htmlMediaController } from 'eyecontact'

const gate = new EyeContact({ mode: 'gaze', graceMs: ${settings.graceMs} })
gate.attach(htmlMediaController(document.querySelector('video')!))
await gate.start()`}
                  </pre>
                  <p className="text-2xs leading-relaxed text-faint">
                    Same engine this page runs on. There are adapters for plain{' '}
                    <code className="font-mono text-muted">&lt;video&gt;</code>, the YouTube IFrame
                    API, and anything else via{' '}
                    <code className="font-mono text-muted">customController</code>.
                  </p>
                </div>
              </Panel>
            </div>

            <aside className="space-y-4">
              <Panel
                title="Camera"
                active={gaze.running && gaze.attentive}
                right={
                  <Tooltip label="Detection rate actually being achieved">
                    <span className="font-mono text-2xs tabular-nums text-faint">
                      {gaze.fps > 0 ? `${gaze.fps.toFixed(0)} fps` : '—'}
                    </span>
                  </Tooltip>
                }
              >
                <CameraPreview
                  gate={gaze.gate}
                  signals={gaze.signals}
                  attentive={gaze.attentive}
                  running={gaze.running}
                  showMesh={settings.showMesh}
                  gazeTolerance={settings.thresholds.gazeTolerance}
                />
              </Panel>

              <Panel title="Tuning">
                <SettingsPanel
                  settings={settings}
                  onChange={patch}
                  onReset={resetSettings}
                  disabled={gaze.running}
                />
              </Panel>

              <Panel title="Status">
                <dl className="grid grid-cols-2 gap-x-3 gap-y-2 p-3.5 font-mono text-2xs">
                  <dt className="text-faint">gate</dt>
                  <dd className="text-right text-muted">{gaze.state}</dd>
                  <dt className="text-faint">reason</dt>
                  <dd className="text-right text-muted">{gaze.reason}</dd>
                  <dt className="text-faint">away for</dt>
                  <dd className="text-right text-muted">{formatMs(gaze.awayForMs)}</dd>
                  <dt className="text-faint">player</dt>
                  <dd className="text-right text-muted">{playing ? 'playing' : 'paused'}</dd>
                </dl>
              </Panel>
            </aside>
          </div>

          <footer className="mt-6 border-t border-line pt-4 text-2xs leading-relaxed text-faint">
            Everything runs in your browser. Camera frames go to the local MediaPipe wasm model and
            nowhere else — no video is uploaded, recorded, or sent anywhere.
          </footer>
        </div>
      </div>
    </TooltipProvider>
  )
}
